import React, { useState, useMemo, useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { Input } from 'src/components/ui/input';
import { useLocationByPincode } from 'src/hooks/useResumeData';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from 'src/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { Button } from 'src/components/ui/button';
import { CheckIcon, ChevronsUpDown } from 'lucide-react';
import { FormField } from './common';

export const Combobox = ({
  options,
  value,
  onChange,
  placeholder,
  error,
  disabled,
  'data-field': dataField,
  id, // <-- accept id
  className,
}) => {
  const [open, setOpen] = useState(false);
  const selectedOption = options?.find((option) => option?.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-field={dataField}
          id={id} // <-- apply id here
          className={`w-full justify-between border border-[var(--border)] bg-[var(--surface)] text-[var(--text)] hover:bg-[var(--surface-muted)] hover:border-[var(--primary)] transition-colors h-10 px-3 ${
            error ? 'border-red-500 focus:border-red-500' : ''
          } ${!value && 'text-[var(--text-faint)]'} ${open ? 'border-ah-brand-primary shadow-[0_0_0_3px_rgba(68,114,196,0.1)]' : ''} ${className}`}
        >
          <span className="text-xs truncate text-left flex-1 min-w-0">
            {selectedOption ? selectedOption.name : placeholder}
          </span>
          <ChevronsUpDown
            className={`ml-2 h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
            style={{ opacity: 0.5 }}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] p-0 border border-[var(--border)] bg-[var(--surface)] text-[var(--text)] shadow-[0_4px_12px_rgba(30,50,100,0.07)] rounded-md overflow-hidden [&_[cmdk-input-wrapper]]:border-[var(--border-light)] [&_[cmdk-input-wrapper]]:px-3 [&_[cmdk-input-wrapper]_svg]:text-[var(--text-faint)] [&_[cmdk-item][data-highlighted]]:bg-[var(--primary-light)] [&_[cmdk-item][data-highlighted]]:text-[var(--primary)] [&_[cmdk-item][aria-selected=true]]:bg-[var(--primary-light)] [&_[cmdk-item][aria-selected=true]]:text-[var(--primary)]"
        align="start"
        sideOffset={4}
      >
        <Command className="rounded-md">
          <CommandInput
            placeholder={`Search ${placeholder.toLowerCase()}...`}
            className="h-9 text-[11px] bg-transparent py-2.5 outline-none placeholder:text-[var(--text-faint)] text-[var(--text)] focus:ring-0"
          />
          <CommandEmpty className="py-6 text-center text-[11px] text-[var(--text-faint)]">
            No results found.
          </CommandEmpty>
          <CommandGroup className="max-h-[200px] overflow-auto p-1">
            {options?.map((option) => {
              const isSelected = value === option.id;
              return (
                <CommandItem
                  key={option?.id}
                  onSelect={() => {
                    onChange(option?.id);
                    setOpen(false);
                  }}
                  value={option.name}
                  aria-selected={isSelected}
                  className={`
                    cursor-pointer text-[11px] leading-tight px-2.5 py-2 rounded-md
                    transition-all duration-150 ease-in-out
                    ${
                      isSelected
                        ? 'bg-[var(--primary-light)] text-[var(--primary)] font-medium'
                        : 'text-[var(--text)]'
                    }
                  `}
                >
                  <div className="flex items-center justify-around px-2 gap-2.5 w-full min-w-0 min-h-8">
                    <span
                      className={`flex items-center justify-center shrink-0 w-4 h-4 rounded-md ${isSelected ? 'bg-ah-brand-primary' : 'bg-ah-surface-card-border'}`}
                    >
                      <CheckIcon className="h-3 w-3 text-white" />
                    </span>
                    <span
                      className={`flex-1 truncate text-left text-sm ${isSelected ? 'text-[var(--primary)]' : 'text-[var(--text)]'}`}
                    >
                      {option.name}
                    </span>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const AddressFields = ({ control, errors, masterData, setValue }) => {
  const [selectedStateId, setSelectedStateId] = useState(null);

  const citiesByState = useMemo(() => {
    if (!selectedStateId) return [];
    const stateData = masterData.locations[selectedStateId];
    return stateData && stateData.cities ? stateData.cities : [];
  }, [selectedStateId, masterData.locations]);

  // When a valid 6-digit PIN code is entered, look up its city + state and
  // auto-fill both dropdowns. The query is cached per-PIN (so the same PIN isn't
  // re-fetched) and disabled when `setValue` isn't provided (read-only usages).
  const zipCode = useWatch({ control, name: 'zipCode' });
  const pin = String(zipCode || '').trim();
  const { data: pincodeLoc } = useLocationByPincode(setValue ? pin : '');
  useEffect(() => {
    if (!setValue || !pincodeLoc) return;
    if (pincodeLoc.state_id != null) {
      setValue('state', pincodeLoc.state_id, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setSelectedStateId(pincodeLoc.state_id);
    }
    if (pincodeLoc.city_id != null) {
      setValue('city', pincodeLoc.city_id, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [pincodeLoc, setValue]);

  return (
    <>
      <div className="grid grid-cols-12 gap-x-4 gap-y-6">
        <div className="col-span-12">
          <FormField
            className="text-xs"
            label="Address"
            id="addressLine1"
            required
          >
            <Controller
              name="addressLine1"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="addressLine1"
                  placeholder="House/Flat No., Building Name"
                  className={`bg-[var(--surface)] text-[var(--text)] placeholder:text-[var(--text-faint)] text-xs ${
                    errors?.addressLine1
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-[var(--border)]'
                  }`}
                />
              )}
            />
          </FormField>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-x-4 gap-y-6">
        <div className="col-span-12">
          <FormField className="text-xs" label="Street" id="street" required>
            <Controller
              name="street"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="street"
                  placeholder="Street Name"
                  className={`bg-[var(--surface)] text-[var(--text)] placeholder:text-[var(--text-faint)] text-xs ${
                    errors?.street
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-[var(--border)]'
                  }`}
                />
              )}
            />
          </FormField>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-x-4 gap-y-6">
        <div className="col-span-12 md:col-span-4">
          <FormField className="text-xs" label="State" id="state" required>
            <Controller
              name="state"
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                React.useEffect(() => {
                  if (field.value) {
                    setSelectedStateId(field.value);
                  }
                }, [field.value]);
                return (
                  <div data-field="state">
                    <Combobox
                      options={masterData.states}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        setSelectedStateId(value);
                      }}
                      placeholder="Select state"
                      error={errors?.state}
                      id="state" // <-- pass id
                      className="text-xs"
                    />
                  </div>
                );
              }}
            />
          </FormField>
        </div>

        <div className="col-span-12 md:col-span-4">
          <FormField className="text-xs" label="City" id="city" required>
            <Controller
              name="city"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div data-field="city">
                  <Combobox
                    options={citiesByState}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select city"
                    error={errors?.city}
                    disabled={!selectedStateId}
                    id="city" // <-- pass id
                    className="text-xs"
                  />
                </div>
              )}
            />
          </FormField>
        </div>

        <div className="col-span-12 md:col-span-4">
          <FormField className="text-xs" label="PIN Code" id="zipCode" required>
            <Controller
              name="zipCode"
              control={control}
              rules={{
                required: true,
                pattern: /^[0-9]{6}$/,
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  id="zipCode"
                  data-field="zipCode"
                  maxLength={6}
                  placeholder="Enter PIN code"
                  className={`bg-[var(--surface)] text-[var(--text)] placeholder:text-[var(--text-faint)] text-xs ${
                    errors?.zipCode
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-[var(--border)]'
                  }`}
                />
              )}
            />
          </FormField>
        </div>
      </div>
    </>
  );
};
